import { Config } from './Config';
import { State } from './State';
import { ApiClient } from '../api/ApiClient';
import { ViewTracker } from '../tracking/ViewTracker';
import { ClickTracker } from '../tracking/ClickTracker';
import { ReadTimeTracker } from '../tracking/ReadTimeTracker';
import { AnnouncementQueue } from '../utils/queue';
import { generateAnonymousId } from '../utils/storage';
import { injectStyles } from '../utils/injectStyles';
import { Popup } from '../ui/Popup';
import { TopBar } from '../ui/TopBar';
import { PersistentTopBar } from '../ui/PersistentTopBar';
import { Toast } from '../ui/Toast';
import { AnnouncementList } from '../ui/AnnouncementList';
import {
    Announcement,
    AnnouncementsResponse,
    BoostType,
    AnnouncementView,
    CurrentUser,
    InitOptions,
    ServerTheme,
    UserInfo,
    MetadataValue,
    UserMetadata,
    ViewType,
} from '../types';

export class PulseRelease {
    private config: Config;
    private state: State;
    private api: ApiClient;
    // Trackers are only constructed in a normal (non-guest) session — guest mode
    // does no analytics at all, so these stay unassigned there and are never
    // referenced (every code path that touches them is gated on `!config.guest`).
    private viewTracker!: ViewTracker;
    private clickTracker!: ClickTracker;
    private readTimeTracker!: ReadTimeTracker;
    private queue: AnnouncementQueue | null = null;
    private currentComponent: Popup | TopBar | PersistentTopBar | Toast | AnnouncementList | null =
        null;
    private persistentTopBar: PersistentTopBar | null = null;
    // Id of the announcement currently shown in the persistent bar, so a poll
    // that re-fetches the same one doesn't tear it down and re-animate it.
    private persistentTopBarId: string | null = null;
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    // Holds the announcement id whose view is being tracked right now by a CTA
    // click, so the close handler that fires immediately after doesn't ALSO track
    // a view for THAT announcement (which double-counted impressions). Scoped to
    // the id — not a global flag — so a slow track for popup #1 can't suppress a
    // legitimate view for a later popup #2.
    private viewInFlightId: string | null = null;
    // Prevents overlapping poll ticks from stacking when the backend is slow.
    private polling = false;
    // Once destroy() runs, a scheduled init retry must not resurrect the instance.
    private destroyed = false;
    // Bounded retry so a transient failure on the very first init() doesn't
    // disable the SDK for the whole page session.
    private initAttempts = 0;
    // Ensures the developer-facing init-failure warning (see handleInitFailure)
    // fires exactly once per attempt SEQUENCE, not once per retry. Reset to
    // false whenever a fresh sequence starts (initAttempts back at 0).
    private initWarned = false;
    // Guards the session-recovery re-init so a persistent auth failure can't loop.
    private recovering = false;
    // Collapses concurrent init() calls (a scheduled retry firing while a
    // manual/recovery init is already running) onto one in-flight promise so we
    // never open two sessions or display the queue twice.
    private initInFlight: Promise<void> | null = null;
    private static readonly MAX_INIT_ATTEMPTS = 3;
    // Coalesced metadata diff, accumulated by setMetadata/removeMetadata/
    // clearMetadata since the last flush and drained as an ordered sequence of
    // RESTful calls (clear -> PATCH set -> DELETE each removed key). Reset once
    // a full init()/identify() round-trip has already transmitted the current
    // state via /init's custom_metadata field.
    private metadataPendingSet: UserMetadata = {};
    private metadataPendingRemove: Set<string> = new Set();
    private metadataPendingClear = false;
    private metadataFlushTimer: ReturnType<typeof setTimeout> | null = null;
    // Serializes the /metadata call sequence onto a single in-flight promise so
    // two flushes never overlap (which could let an older, slower call land last
    // and revert the newer state). A flush requested while one is in flight
    // awaits it, and the completion handler re-arms if anything is still pending.
    private metadataFlushInFlight: Promise<void> | null = null;
    // Bumped on every metadata mutation. Captured in a flush snapshot so the
    // completion handler only retires pending remove entries that weren't
    // re-issued during the round-trip.
    private metadataGeneration = 0;
    // Bumped only by clearMetadata(). Captured in a flush snapshot so a second
    // clear() issued mid-flight isn't dropped by the first flush's completion.
    private metadataClearSeq = 0;
    // Consecutive flush failures, for the bounded retry backoff. Reset on any
    // successful flush.
    private metadataFlushFailures = 0;
    private static readonly METADATA_FLUSH_DELAY_MS = 400;
    private static readonly METADATA_FLUSH_RETRY_BASE_MS = 3000;
    private static readonly MAX_METADATA_FLUSH_RETRY_MS = 60000;
    private static readonly MAX_METADATA_FLUSH_RETRIES = 5;

    // Tracks which mutators have already emitted their one-time "unavailable in
    // guest mode" warning, so the warning fires ONCE per method, not per call.
    private guestWarned: Set<string> = new Set();

    constructor(options: InitOptions) {
        this.config = new Config(options);
        this.state = new State(this.config.apiKey, this.config.guest);
        this.api = new ApiClient(this.config);

        // Guest mode does no analytics — skip building the trackers entirely so
        // they can never be started or fire a request.
        if (!this.config.guest) {
            // Route a tracker send failure through the same session-recovery path
            // as the poll loop, so a 401 on a view/click/read-time send
            // re-establishes the session instead of failing forever.
            // maybeRecoverSession filters to 401s and is guarded against looping.
            const onAuthError = (error: unknown): void => {
                void this.maybeRecoverSession(error);
            };
            this.viewTracker = new ViewTracker(this.api, this.config, onAuthError);
            this.clickTracker = new ClickTracker(this.api, this.config, onAuthError);
            this.readTimeTracker = new ReadTimeTracker(this.api, this.config, onAuthError);
        }
    }

    /**
     * Initialize the SDK and start displaying announcements.
     *
     * Errors are caught and logged — a vendor outage must never crash the
     * customer's page. Callers that need to know whether init actually
     * completed can call `PulseRelease.isInitialized()` on the public API;
     * that helper now returns true only when an instance exists AND its
     * `isReady()` flag is set, so a failed init reports as not initialized.
     * Methods that depend on initialization no-op when it failed.
     */
    public async init(): Promise<void> {
        // Dedupe overlapping init() calls onto a single in-flight promise.
        if (this.initInFlight) {
            return this.initInFlight;
        }

        this.initInFlight = this.runInit();
        try {
            await this.initInFlight;
        } finally {
            this.initInFlight = null;
        }
    }

    private async runInit(): Promise<void> {
        // A fresh init() intentionally revives the instance: clear the
        // destroyed flag up front so this call proceeds. A destroy() that lands
        // AFTER this point (while an await below is in flight) re-sets the flag,
        // and the post-await guards abort cleanly — the two cases are
        // distinguished purely by whether destroy() ran before or after here.
        this.destroyed = false;

        // Guest mode takes a completely separate, session-less path: no
        // /sessions call, no anonymous id, no polling, no trackers, no boosts.
        if (this.config.guest) {
            await this.runGuestInit();
            return;
        }

        // A fresh attempt sequence (as opposed to a scheduled retry of this
        // same sequence) gets a clean slate for the one-time developer
        // warning below — see handleInitFailure.
        if (this.initAttempts === 0) {
            this.initWarned = false;
        }

        try {
            // Inject styles into the document. The server-driven application
            // theme is applied later, once /init responds.
            injectStyles();

            // Re-attach a previously persisted session token so subsequent
            // requests authenticate even on first call after a page refresh.
            const persistedToken = this.state.getSessionToken();
            if (persistedToken) {
                this.api.setSessionToken(persistedToken);
            }

            // If already initialized, just refresh announcements
            if (this.state.isInitialized) {
                this.config.log('SDK already initialized, refreshing announcements...');
                await this.fetchAndDisplayAnnouncements();
                return;
            }

            this.config.log('Initializing SDK...');

            // Get or create anonymous ID
            let anonymousId = this.state.getAnonymousId();
            if (!anonymousId) {
                anonymousId = generateAnonymousId();
                this.state.setAnonymousId(anonymousId);
            }

            // Order this /sessions write (which conveys custom_metadata) after any
            // in-flight /metadata flush step: /init and the flush both write the
            // server bag, and letting them overlap can lose an update
            // (e.g. an in-flight DELETE landing after this POST). Draining first
            // also makes the reconcile below's snapshot assumptions hold.
            await this.drainMetadataFlush();

            // A destroy() may have landed while the flush drained; bail before
            // opening a session for a torn-down instance.
            if (this.destroyed) {
                return;
            }

            // Snapshot the metadata this /init actually transmits as
            // custom_metadata BEFORE the round-trip. api.init() serializes the
            // body synchronously, so this equals what the server will merge —
            // used below to retire only the conveyed SET entries.
            const sentMetadata = { ...this.config.metadata };

            // Initialize with backend
            const initResponse = await this.api.init(anonymousId);

            // A destroy() (e.g. a React StrictMode remount) may have landed
            // while the /init round-trip was pending. Abort cleanly — no state
            // writes, no persisted session token, no polling — so the
            // torn-down instance stays dead instead of resurrecting as a zombie
            // that polls forever with a nulled token.
            if (this.destroyed) {
                return;
            }

            // A malformed 2xx with no `user` can't establish a session. This is
            // a deterministic server payload bug, not a transient blip — abort
            // gracefully (SDK stays not-ready) rather than dereferencing
            // undefined and spinning the bounded retry against a bug no retry
            // can fix. A later init() can try again once the server is fixed.
            if (!initResponse || !initResponse.user) {
                this.config.error('Init response missing user; aborting init.', initResponse);
                return;
            }

            // Apply the application's branding (accent, corner style, color mode)
            // from the server.
            this.applyServerTheme(initResponse.theme);

            // Update state with user info
            this.state.setUser(initResponse.user.id, initResponse.user.is_anonymous);
            this.state.setUnreadCount(initResponse.unread_count || 0);
            this.state.setIsFreePlan(initResponse.is_free_plan);
            this.state.setShowBranding(initResponse.show_branding !== false);

            // Persist the session token and arm the ApiClient with it.
            if (initResponse.session_token) {
                this.state.setSessionToken(initResponse.session_token);
                this.api.setSessionToken(initResponse.session_token);
            }

            this.state.setInitialized(true);
            // This /init merged the sent SET entries server-side. Retire only
            // those; any queued remove/clear (which /init cannot convey) — or a
            // set that raced in during the round-trip — survives and flushes.
            this.reconcileMetadataAfterInit(sentMetadata);
            // Init structurally succeeded (session established); clear the retry
            // counter and ARM POLLING BEFORE the first fetch. If that fetch throws,
            // the SDK is still initialized and the poll loop will retry it on its
            // own cadence — rather than being left initialized-but-never-polling.
            this.initAttempts = 0;
            this.startPolling();

            this.config.log('SDK initialized successfully', initResponse.user);

            // Fetch and display announcements
            await this.fetchAndDisplayAnnouncements();
        } catch (error) {
            this.config.error('Failed to initialize SDK:', error);
            this.handleInitFailure(error);
        }
    }

    /**
     * Guest-mode initialization: a read-only, session-less, zero-client-state
     * bring-up. Injects styles (so the on-demand list can render) and marks the
     * SDK ready — nothing else. Deliberately establishes NO backend session (no
     * SdkUser is ever created), reads/writes NO cookie or storage identity,
     * starts NO poll timer and NO trackers, and fetches/renders NO boost. Only
     * `openAnnouncements()` is serviced afterwards.
     */
    private async runGuestInit(): Promise<void> {
        injectStyles();

        if (this.state.isInitialized) {
            this.config.log('Guest SDK already initialized');
            return;
        }

        this.config.log('Initializing SDK in guest mode (read-only, on-demand)...');

        // Bootstrap against the unified /init endpoint in guest mode to fetch the
        // application branding, then apply it via the SAME path standard mode
        // uses — so any later guest UI (list, popups) is themed from the first
        // paint. A failure (network, or a non-Pro app that ships no custom
        // branding) must NEVER block guest bring-up: swallow it, leave the
        // stylesheet defaults in place, and still mark the SDK ready.
        try {
            const bootstrap = await this.api.init();
            this.applyServerTheme(bootstrap.theme);
        } catch (error) {
            this.config.log('Guest theme bootstrap failed; using default styling', error);
        }

        // A destroy() may have landed while the bootstrap was in flight; don't
        // mark a torn-down guest instance ready.
        if (this.destroyed) {
            return;
        }

        // Mark ready so the public API (openAnnouncements, etc.) is serviceable.
        // State.setInitialized no-ops its storage write under guest mode.
        this.state.setInitialized(true);
        this.config.log('SDK initialized in guest mode');
    }

    /**
     * Emit the one-time "unavailable in guest mode" warning for a mutator that
     * was called on a guest session, then let the caller no-op. The warning
     * fires at most once per method name for the lifetime of the instance.
     */
    private warnGuestUnavailable(method: string): void {
        if (this.guestWarned.has(method)) {
            return;
        }
        this.guestWarned.add(method);
        this.config.warn(`[PulseRelease] ${method}() is unavailable in guest mode.`);
    }

    /**
     * Whether this SDK instance was initialized in guest mode (read-only,
     * session-less). True iff `init({ guest: true })` was used.
     */
    public isGuest(): boolean {
        return this.config.guest;
    }

    /**
     * Handle a failed init() attempt: emit exactly one ungated,
     * developer-facing warning per attempt SEQUENCE (never once per retry —
     * see the `initWarned` reset in runInit), then either fail fast or fall
     * back to the existing bounded retry.
     *
     * 401/403 here come from the `/sessions` call, which ValidateSdkApiKey
     * gates purely on the API key and Origin header — that call never sends
     * the session token (see ApiClient.init's `skipAuth`), so this can NEVER
     * be a stale-token situation. It's therefore deterministic: retrying the
     * same key/origin 3× over ~14s cannot change the outcome, so we fail fast
     * instead. This holds even when this init() is itself a session-recovery
     * re-init (see maybeRecoverSession) — that path 401s on OTHER endpoints
     * (which DO send the token) and reacts by re-running init(), landing back
     * here; a 401 from THIS call still only ever means the key/origin itself
     * is now rejected. Anything else (network blip, timeout, 5xx) keeps the
     * existing bounded retry, since those are plausibly transient.
     *
     * A failure while the SDK is already initialized is just a refresh
     * hiccup, not a real init failure — scheduleInitRetry() already no-ops in
     * that case — so it's routed straight there without a developer warning.
     */
    private handleInitFailure(error: unknown): void {
        if (this.state.isInitialized) {
            this.scheduleInitRetry();
            return;
        }

        if (!this.initWarned) {
            this.initWarned = true;
            this.config.warn(PulseRelease.describeInitFailure(error));
        }

        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) {
            // Deterministic auth failure: no retry can fix a rejected key or a
            // blocked origin. Reset the counter so a LATER manual init() call
            // (e.g. after the developer fixes their config) is treated as a
            // fresh sequence rather than inheriting a stale attempt count.
            this.initAttempts = 0;
            return;
        }

        this.scheduleInitRetry();
    }

    /**
     * Build the single, always-on developer warning for a failed init()
     * attempt sequence — see {@link handleInitFailure}. Distinguishes the two
     * deterministic auth failures the ValidateSdkApiKey middleware can return
     * (401: bad/missing/unknown key; 403: origin not allowed, or application
     * disabled) from everything else, which gets a generic "couldn't reach
     * the API" message carrying the status when one is available.
     */
    private static describeInitFailure(error: unknown): string {
        const status = (error as { status?: number } | null)?.status;

        if (status === 401) {
            return 'PulseRelease: API key rejected (401). Check that `apiKey` is your public key starting with `pr_`.';
        }

        if (status === 403) {
            return "PulseRelease: this origin is not allowed for your application (403). Add this domain to your application's allowed domain in the dashboard.";
        }

        if (typeof status === 'number') {
            return `PulseRelease: couldn't reach the API (status ${status}).`;
        }

        if (error instanceof Error && error.name === 'TimeoutError') {
            return "PulseRelease: couldn't reach the API (request timed out).";
        }

        const detail = error instanceof Error && error.message ? error.message : 'network error';
        return `PulseRelease: couldn't reach the API (${detail}).`;
    }

    /**
     * Re-identify the current session as a (freshly) known user — typically
     * called right after the customer's own login completes. Updates the user
     * context, re-establishes the backend session for that user, and refreshes
     * the announcements on offer — all WITHOUT wiping the persisted seen-set or
     * anonymous id (so the user's read history survives) and without tearing down
     * the poll loop or trackers.
     *
     * When init() hasn't completed yet, this just seeds the user context and runs
     * the normal init().
     *
     * Include `user.hash` when the application requires identity
     * verification — a fresh HMAC of the identifier being set, computed by your
     * backend (see {@link UserInfo.hash}). It replaces any previously
     * configured hash so the re-established session carries the proof for the
     * newly identified user.
     *
     * A non-object argument (e.g. a customer's own possibly-null auth state
     * passed straight through) is ignored with a warning rather than thrown.
     */
    public async identify(user: UserInfo): Promise<void> {
        // Guest mode has no visitor identity to update; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('identify');
            return;
        }

        // Runtime guard even though the type requires a UserInfo: destructuring
        // null/undefined below would throw, and since callers commonly fire
        // identify() without awaiting it, that throw would reject unhandled
        // into the host page.
        if (user === null || typeof user !== 'object' || Array.isArray(user)) {
            this.config.warn('PulseRelease: identify() requires a user object.');
            return;
        }

        // Merge into the user context ApiClient reads when building the /init body.
        // Metadata and the identity hash are handled separately (below) — neither
        // is part of the id/email `user` context ApiClient sends.
        const { metadata, hash, ...identity } = user;
        this.config.user = { ...this.config.user, ...identity };

        // A new identity needs its own signature; adopt the supplied hash so the
        // /init below can prove the freshly claimed id/email.
        if (hash !== undefined) {
            this.config.hash = hash;
        }

        if (metadata) {
            for (const [key, value] of Object.entries(metadata)) {
                this.applyMetadataEntry(key, value, false);
            }
        }

        // identify() is a documented resume point for a metadata sync that had
        // hit the retry cap: reset the failure counter so its /init (and any
        // follow-up flush) restarts the backoff series from zero.
        this.metadataFlushFailures = 0;

        if (!this.state.isInitialized) {
            await this.init();
            return;
        }

        try {
            this.config.log('Identifying user; re-establishing session', this.config.user);

            // Order this /sessions write after any in-flight /metadata flush step
            // so the two never race on the server bag (see runInit).
            await this.drainMetadataFlush();

            // Pass the anonymous id only so /init can match an existing anonymous
            // row when no user identity is present. Once a user_id/email IS sent,
            // the server matches strictly on user_id > email > anonymous_id and
            // does NOT merge the prior anonymous activity into the identified user
            // (the anonymous row is left orphaned); this is intentional and not
            // relied upon here.
            const anonymousId = this.state.getAnonymousId() ?? undefined;

            // Snapshot what this /init transmits (see runInit) so the reconcile
            // below only retires the conveyed SET entries.
            const sentMetadata = { ...this.config.metadata };
            const initResponse = await this.api.init(anonymousId);

            this.applyServerTheme(initResponse.theme);
            this.state.setUser(initResponse.user.id, initResponse.user.is_anonymous);
            this.state.setUnreadCount(initResponse.unread_count || 0);
            this.state.setIsFreePlan(initResponse.is_free_plan);
            this.state.setShowBranding(initResponse.show_branding !== false);

            if (initResponse.session_token) {
                this.state.setSessionToken(initResponse.session_token);
                this.api.setSessionToken(initResponse.session_token);
            }

            // This /init merged the sent SET entries server-side. A removeMetadata/
            // clearMetadata issued before/during identify() (e.g. a normal login:
            // removeMetadata('beta') then identify(user)) is NOT conveyed by /init
            // and must survive to a real /metadata flush.
            this.reconcileMetadataAfterInit(sentMetadata);

            // Trackers and the poll loop keep running untouched; just refresh what
            // the identified user should now see.
            await this.fetchAndDisplayAnnouncements();
        } catch (error) {
            // Degrade gracefully: on failure the previous (still-valid) session
            // stays in place rather than the SDK breaking.
            this.config.error('Failed to identify user:', error);
        }
    }

    /**
     * Retry a failed init() with exponential backoff, up to a small cap, so a
     * transient backend blip on first page load doesn't leave the SDK dead for
     * the whole session. No-op once initialized or destroyed.
     */
    private scheduleInitRetry(): void {
        if (this.destroyed || this.state.isInitialized) {
            return;
        }

        if (this.initAttempts >= PulseRelease.MAX_INIT_ATTEMPTS) {
            this.config.error(
                `SDK init failed after ${this.initAttempts} retries; giving up for this session.`
            );
            return;
        }

        this.initAttempts++;
        const delay = Math.min(30000, 2000 * 2 ** (this.initAttempts - 1));
        this.config.log(
            `Retrying SDK init in ${delay}ms (attempt ${this.initAttempts}/${PulseRelease.MAX_INIT_ATTEMPTS})`
        );

        setTimeout(() => {
            if (!this.destroyed && !this.state.isInitialized) {
                void this.init();
            }
        }, delay);
    }

    /**
     * Periodically re-check the API for new or updated announcements so SPAs —
     * which never reload the page — still surface them. Idempotent: a second
     * call replaces any existing timer. No-op when polling is disabled
     * (pollInterval = 0).
     */
    private startPolling(): void {
        if (this.config.pollInterval <= 0) {
            return;
        }

        this.stopPolling();

        this.pollTimer = setInterval(() => {
            void this.pollForAnnouncements();
        }, this.config.pollInterval);
    }

    private stopPolling(): void {
        if (this.pollTimer !== null) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /**
     * Validate a single metadata value through the same rules as Config's
     * batch sanitizer (see `Config.sanitizeMetadata`), mutate `config.metadata`
     * synchronously, and — when `trackPending` — record it in the pending diff
     * for the next debounced flush. `trackPending` is false for identify()'s
     * merge, whose values are transmitted via the immediately-following /init
     * call instead of a separate /metadata request.
     */
    private applyMetadataEntry(name: string, rawValue: unknown, trackPending: boolean): void {
        if (typeof name !== 'string' || name.length === 0) {
            return;
        }

        const { accepted, ignored } = Config.sanitizeMetadata({ [name]: rawValue });

        if (ignored.length > 0) {
            this.config.log('Dropped invalid metadata value for key:', name);
            return;
        }

        // sanitizeMetadata strips invalid characters from the key, so the
        // accepted entry is stored under the canonical (stripped) key, which may
        // differ from `name`. Route everything below through that key so the
        // pending diff — and any DELETE /metadata/{key} — matches what was set.
        const key = Object.keys(accepted)[0];

        const isNewKey = !(key in this.config.metadata);
        if (isNewKey && Object.keys(this.config.metadata).length >= Config.MAX_METADATA_KEYS) {
            this.config.log('Metadata key limit reached; dropping key:', name);
            return;
        }

        this.config.metadata[key] = accepted[key];

        if (trackPending) {
            this.metadataPendingRemove.delete(key);
            this.metadataPendingSet[key] = accepted[key];
            this.metadataGeneration++;
        }
    }

    /**
     * (Re)arm a single debounced timer that batches every setMetadata /
     * removeMetadata / clearMetadata call since the last flush into one ordered
     * /metadata call sequence. No-ops before init() has established a session — the
     * change already lives in `config.metadata`, and the next `/init` call
     * carries it as `custom_metadata` instead of firing an orphan request.
     *
     * @param delayMs override for the debounce delay; the failure path passes a
     *   backoff so a stale server eventually reconciles without hot-looping.
     */
    private scheduleMetadataFlush(delayMs: number = PulseRelease.METADATA_FLUSH_DELAY_MS): void {
        if (!this.state.isInitialized) {
            return;
        }

        this.clearMetadataFlushTimer();

        this.metadataFlushTimer = setTimeout(() => {
            this.metadataFlushTimer = null;
            void this.flushMetadata();
        }, delayMs);
    }

    /**
     * Send the coalesced metadata diff accumulated since the last flush.
     *
     * Flushes are serialized onto a single in-flight promise so two call
     * sequences never overlap — otherwise an older, slower call could land last
     * and revert the newer server state. A flush requested while one is in flight
     * just awaits the current one; the completion handler re-arms if changes
     * remain.
     *
     * Best-effort: a failure is logged, the diff is retained, and a bounded
     * backoff retry is armed so the server eventually reconciles — it never
     * rejects into the caller.
     */
    /**
     * Block until no /metadata flush step is in flight, so a caller that is about
     * to issue its own server-side write of the metadata bag (identify()/runInit()
     * via /init's custom_metadata) can't race an overlapping flush call and lose
     * an update. Loops because the flush's completion handler may re-arm a follow-up
     * flush; each awaited promise is the exact one stored in metadataFlushInFlight
     * and cleared in flushMetadata's finally before this re-reads it, so it cannot
     * deadlock. Never rejects — performMetadataFlush swallows its own errors.
     */
    private async drainMetadataFlush(): Promise<void> {
        while (this.metadataFlushInFlight) {
            await this.metadataFlushInFlight;
        }
    }

    private async flushMetadata(): Promise<void> {
        if (this.metadataFlushInFlight) {
            return this.metadataFlushInFlight;
        }

        this.metadataFlushInFlight = this.performMetadataFlush();
        try {
            await this.metadataFlushInFlight;
        } finally {
            this.metadataFlushInFlight = null;
        }
    }

    /**
     * Drain the current pending diff as an ORDERED sequence of RESTful calls and
     * reconcile the pending state against what each call actually applied. Never
     * called directly — {@link flushMetadata} wraps it in the in-flight guard so
     * the whole sequence runs without another flush overlapping it.
     *
     * The server applies clear -> set -> remove, so the SDK must issue them in
     * that order: DELETE /metadata, then PATCH /metadata, then DELETE
     * /metadata/{key} per removed key. Progress is retired incrementally — each
     * pending entry is cleared only once its own call returns — so a partial
     * failure (e.g. clear succeeds, then PATCH throws) does NOT re-issue the
     * already-applied step on retry, yet still retains every not-yet-applied one.
     */
    private async performMetadataFlush(): Promise<void> {
        // Snapshot the diff AND the mutation generations, so each step can tell
        // which pending entries it covered vs. ones that raced in during the
        // sequence.
        const generation = this.metadataGeneration;
        const clearSeq = this.metadataClearSeq;
        const set = { ...this.metadataPendingSet };
        const remove = Array.from(this.metadataPendingRemove);
        const clear = this.metadataPendingClear;

        if (Object.keys(set).length === 0 && remove.length === 0 && !clear) {
            return;
        }

        try {
            // Step 1 — clear. Retire the pending clear flag only once its DELETE
            // returns, and only when no NEW clear() raced in (clearSeq stable),
            // so a second clear mid-flight isn't lost. Once retired, a retry
            // after a later step fails will NOT re-issue this clear.
            if (clear) {
                await this.api.clearAllMetadata();
                if (this.metadataClearSeq === clearSeq) {
                    this.metadataPendingClear = false;
                }
            }

            // Step 2 — set. Retire set keys by value-equality only after the
            // PATCH returns, so a racing re-set to a new value survives. Reflect
            // server-side drops (allowlist / limits) locally so getUser() matches
            // what actually targets and we stop re-sending rejected keys.
            if (Object.keys(set).length > 0) {
                const response = await this.api.patchMetadata(set);
                Object.keys(set).forEach((key) => {
                    if (this.metadataPendingSet[key] === set[key]) {
                        delete this.metadataPendingSet[key];
                    }
                });
                this.pruneServerIgnoredKeys(response?.ignored_keys, set);
            }

            // Step 3 — remove. Retire each key only once its own DELETE returns,
            // and only when no mutation raced in (generation stable) — a
            // re-issued remove would otherwise be dropped; re-sending a DELETE is
            // idempotent, so keeping it when unsure is safe.
            for (const key of remove) {
                try {
                    await this.api.deleteMetadataKey(key);
                } catch (error) {
                    // A 404 means the key is already absent server-side — which is
                    // the exact end state a DELETE wants — so treat it as success.
                    // Otherwise a stray/legacy key whose URL the server rejects
                    // would throw here every retry and permanently wedge the queue.
                    if ((error as { status?: number } | null)?.status !== 404) {
                        throw error;
                    }
                }
                if (this.metadataGeneration === generation) {
                    this.metadataPendingRemove.delete(key);
                }
            }

            // Whole sequence landed — reset the failure backoff.
            this.metadataFlushFailures = 0;

            // A change that raced in during the sequence (or a retained
            // remove/clear) still needs sending.
            this.rearmMetadataFlushIfPending();
        } catch (error) {
            // Retain the not-yet-applied diff and re-arm with a bounded backoff
            // so the server eventually reconciles instead of staying stale for
            // the session. Any step that already returned has retired its own
            // pending entry above, so the retry resumes from where this failed.
            this.config.error('Failed to sync metadata changes:', error);
            // A 401 here means the session token expired; recover it so the
            // retained diff can actually land on the next flush instead of
            // 401ing forever. No-ops on any non-401 error.
            void this.maybeRecoverSession(error);
            this.scheduleMetadataFlushRetry();
        }
    }

    /**
     * Prune keys the server reported as ignored (over an allowlist / limits)
     * from local state, and surface them via the debug log, so getUser() mirrors
     * server reality and we don't keep re-sending keys that will be re-rejected.
     *
     * The `ignored_keys` reflect the values in `sentSet` (the PATCH this response
     * answered), which may be stale by now — a newer setMetadata for the same key
     * could have raced in. So prune a key only where the current value still
     * equals what was sent (mirroring the value-equality retire used for the
     * pending set); a raced-in newer value is left intact to flush again.
     *
     * @param sentSet the set map this PATCH actually transmitted.
     */
    private pruneServerIgnoredKeys(ignoredKeys: string[] | undefined, sentSet: UserMetadata): void {
        if (!ignoredKeys || ignoredKeys.length === 0) {
            return;
        }

        this.config.log(
            'Server ignored metadata keys (allowlist/limits); pruning locally:',
            ignoredKeys
        );

        for (const key of ignoredKeys) {
            if (this.metadataPendingSet[key] === sentSet[key]) {
                delete this.metadataPendingSet[key];
            }
            if (this.config.metadata[key] === sentSet[key]) {
                delete this.config.metadata[key];
            }
        }
    }

    /**
     * Re-arm the debounced flush when the pending diff still holds anything —
     * used after a successful flush to pick up changes that raced in during the
     * POST, or a remove/clear the /init reconcile couldn't convey.
     */
    private rearmMetadataFlushIfPending(): void {
        if (
            Object.keys(this.metadataPendingSet).length > 0 ||
            this.metadataPendingRemove.size > 0 ||
            this.metadataPendingClear
        ) {
            this.scheduleMetadataFlush();
        }
    }

    /**
     * Arm a bounded-backoff retry after a failed flush, capped so an offline
     * page doesn't hot-loop. Once the cap is hit we pause until the next
     * metadata change or identify() re-arms a flush.
     */
    private scheduleMetadataFlushRetry(): void {
        this.metadataFlushFailures++;

        if (this.metadataFlushFailures > PulseRelease.MAX_METADATA_FLUSH_RETRIES) {
            this.config.error(
                `Metadata sync failed ${this.metadataFlushFailures - 1} times; pausing retries until the next change or identify().`
            );
            return;
        }

        const delay = Math.min(
            PulseRelease.MAX_METADATA_FLUSH_RETRY_MS,
            PulseRelease.METADATA_FLUSH_RETRY_BASE_MS * 2 ** (this.metadataFlushFailures - 1)
        );

        this.config.log(
            `Retrying metadata sync in ${delay}ms (attempt ${this.metadataFlushFailures}/${PulseRelease.MAX_METADATA_FLUSH_RETRIES})`
        );

        this.scheduleMetadataFlush(delay);
    }

    /**
     * Retire the metadata SET entries a just-completed /init actually conveyed,
     * without ever touching a pending remove/clear (which /init's set-only merge
     * cannot express). Any set that raced in during the round-trip, or a queued
     * remove/clear, is left pending and a flush is armed to carry it.
     *
     * @param sentMetadata the custom_metadata the /init payload transmitted.
     */
    private reconcileMetadataAfterInit(sentMetadata: UserMetadata): void {
        const hasPendingClear = this.metadataPendingClear;

        // When a clear is still pending, the queued SETs must be re-applied AFTER
        // the clear reaches the server (flush order is clear -> set -> remove), so
        // don't retire them here either — otherwise the flush's clear would wipe
        // values /init had merged.
        if (!hasPendingClear) {
            for (const key of Object.keys(sentMetadata)) {
                if (this.metadataPendingSet[key] === sentMetadata[key]) {
                    delete this.metadataPendingSet[key];
                }
            }
        }

        if (
            Object.keys(this.metadataPendingSet).length > 0 ||
            this.metadataPendingRemove.size > 0 ||
            this.metadataPendingClear
        ) {
            this.scheduleMetadataFlush();
        } else {
            this.clearMetadataFlushTimer();
        }
    }

    private clearMetadataFlushTimer(): void {
        if (this.metadataFlushTimer !== null) {
            clearTimeout(this.metadataFlushTimer);
            this.metadataFlushTimer = null;
        }
    }

    /**
     * One poll tick: refresh the announcement data, and start a display cycle
     * only when nothing is currently on screen. Refreshing while a popup is open
     * (or a queue is mid-flight) would interrupt the user, so in that case we
     * just update the cached data and let the next idle tick display anything
     * new. Already-seen boosts never re-pop — that's guarded by the seen set.
     */
    private async pollForAnnouncements(): Promise<void> {
        // Skip if not ready, or if a previous tick is still in flight — otherwise
        // slow requests stack up into a backlog of overlapping polls.
        if (!this.state.isInitialized || this.polling) {
            return;
        }

        this.polling = true;

        try {
            // Keep the unread badge fresh — the count otherwise only changes at
            // init()/markAsRead(), so a SPA that stays open would show a stale
            // number as new announcements land.
            await this.refreshUnreadCount();

            // Something is on screen — just refresh the cached data; displaying
            // now would interrupt the user.
            if (this.currentComponent) {
                await this.fetchBoostedAnnouncements();
                return;
            }

            // Nothing on screen but a queue is still mid-flight (e.g. the user
            // dismissed the current item via close(), or opened the list,
            // neither of which advances the queue). Refresh data and resume it,
            // rather than leaving the queue — and all future auto-display —
            // wedged for the rest of the session.
            if (this.queue?.hasNext()) {
                await this.fetchBoostedAnnouncements();
                await this.displayNextInQueue();
                return;
            }

            // Idle: drop any exhausted-but-non-null queue and start fresh.
            this.queue = null;
            await this.fetchAndDisplayAnnouncements();
        } catch (error) {
            this.config.error('Announcement poll failed:', error);
            await this.maybeRecoverSession(error);
        } finally {
            this.polling = false;
        }
    }

    /**
     * Refresh the cached unread count from the server. Best-effort: a failure
     * here must not abort the poll tick or bubble up, so the badge simply keeps
     * its previous value until the next tick.
     */
    private async refreshUnreadCount(): Promise<void> {
        if (!this.state.userId) {
            return;
        }

        try {
            const count = await this.api.getUnreadCount();
            if (this.state.isInitialized) {
                this.state.setUnreadCount(count);
            }
        } catch (error) {
            this.config.error('Failed to refresh unread count:', error);
        }
    }

    /**
     * If a request failed because the session is no longer valid (401 — an
     * expired/missing session token, e.g. after the end-user was removed or the
     * token rotated), transparently re-init to obtain a fresh token instead of
     * leaving the SDK permanently broken. The server now returns 401 (not 404)
     * for a missing session, so 404 no longer triggers recovery. Guarded so it
     * can't loop.
     */
    private async maybeRecoverSession(error: unknown): Promise<void> {
        const status = (error as { status?: number } | null)?.status;
        if (status !== 401 || this.recovering || this.destroyed) {
            return;
        }

        this.recovering = true;
        try {
            this.config.log('Session no longer valid; re-initializing for a fresh token');
            this.stopPolling();
            this.api.setSessionToken(null);
            this.state.setSessionToken(null);
            this.state.setInitialized(false);
            this.initAttempts = 0;
            await this.init();
        } catch (e) {
            this.config.error('Session recovery failed:', e);
        } finally {
            this.recovering = false;
        }
    }

    /**
     * Whether init() succeeded. Public API guards on this so a failed init
     * (timeout, backend down) degrades silently instead of throwing.
     */
    public isReady(): boolean {
        return this.state.isInitialized;
    }

    /**
     * Whether an init() round-trip is currently in flight — started but not yet
     * settled. Distinct from isReady(): true only during the init window. Lets a
     * caller (the public facade) bring up a loading UI for an openAnnouncements()
     * that arrived before init finished, instead of no-op'ing it.
     */
    public isInitializing(): boolean {
        return this.initInFlight !== null;
    }

    /**
     * Get the unread count for current user (synchronous, from state)
     */
    public getUnreadCount(): number {
        return this.state.getUnreadCount();
    }

    /**
     * Open the announcements list modal.
     *
     * The modal is brought up in a loading state immediately, so the click
     * feels instant even when init() hasn't finished yet:
     *  - Ready: open and populate straight away (the common case).
     *  - Init still in flight: show the loading modal NOW and populate it the
     *    moment init settles (or show a translated error state if init fails).
     *  - Neither ready nor initializing (init never ran, or already failed and
     *    gave up): there is no session and nothing is coming, so this preserves
     *    the explicit "not initialized" error for direct callers. The public
     *    facade guards this path and simply no-ops for customer code.
     */
    public async openAnnouncements(): Promise<void> {
        if (this.state.isInitialized) {
            return this.showAnnouncementsList(1, 10);
        }

        if (this.initInFlight) {
            return this.openAnnouncementsDuringInit();
        }

        this.ensureInitialized();
    }

    /**
     * Serve an openAnnouncements() that arrived while init() is still running:
     * mount the list modal synchronously in its loading state (overlay up,
     * scroll lock engaged), then hand off to it once the in-flight init settles.
     * On success the same modal is fetched and populated in place; on failure it
     * shows a translated error state with the close affordances still working.
     */
    private async openAnnouncementsDuringInit(): Promise<void> {
        const inFlight = this.initInFlight;

        // Bring the loading modal up right away. Nothing else is open during
        // init, so this only acquires the scroll lock and mounts the overlay.
        const list = this.mountLoadingList();

        // init() catches its own failures and never rejects; await defensively.
        try {
            await inFlight;
        } catch {
            // Handled via the isInitialized check below.
        }

        // Torn down (destroy() landed while we waited), or the user closed the
        // modal / it was superseded by another surface: leave it alone.
        if (this.destroyed || this.currentComponent !== list) {
            return;
        }

        if (!this.state.isInitialized) {
            // Init failed while the user was waiting — show a clear error state
            // rather than an endless spinner.
            this.config.error('Failed to open announcements: init did not complete');
            list.isLoading = false;
            list.hasError = true;
            list.updateContent();
            return;
        }

        // Ready now — fetch and populate the SAME modal in place.
        await this.populateAnnouncementsList(list, 1, 10);
    }

    /**
     * Show the announcements list modal starting at a specific page. The public
     * `openAnnouncements()` always opens page 1; the "back to list" affordance
     * on a popup reuses this to reopen the list at the exact page the user left.
     *
     * The new list is shown BEFORE the previously visible component is hidden so
     * the shared body-scroll lock is held continuously across the hand-off — a
     * popup → list return never releases the lock and never jumps the host page.
     */
    private async showAnnouncementsList(page: number = 1, perPage: number = 10): Promise<void> {
        this.ensureInitialized();

        const list = this.mountLoadingList();
        await this.populateAnnouncementsList(list, page, perPage);
    }

    /**
     * Build the list modal, show it in its loading state, and make it the
     * current component. The list is shown BEFORE the previously visible
     * component is hidden so the shared scroll lock never drops to zero across
     * the hand-off.
     */
    private mountLoadingList(): AnnouncementList {
        const list = new AnnouncementList(
            this.config,
            [],
            undefined,
            () => this.handleComponentClose(),
            (announcement) =>
                this.fireAndForget(
                    this.handleAnnouncementClick(announcement),
                    'Announcement click handler failed'
                ),
            async (p: number, pp: number) => await this.fetchAllAnnouncements(p, pp),
            this.state.getShowBranding()
        );
        list.isLoading = true;
        list.show();

        // Hide whatever was open (a returning popup, or nothing) only after the
        // list is up, so the scroll lock never drops to zero in between.
        this.closeCurrentComponent();
        this.currentComponent = list;

        this.config.log('Announcements list opened with loading state');

        return list;
    }

    /**
     * Fetch the requested page and populate an already-mounted list modal in
     * place. On failure the modal stays open showing a translated error state
     * (not an endless spinner, and not a misleading empty state) so the user who
     * asked for it still has working close affordances.
     */
    private async populateAnnouncementsList(
        list: AnnouncementList,
        page: number,
        perPage: number
    ): Promise<void> {
        try {
            // Guest mode is read-only: fetch the list from the public guest
            // endpoint and do NOT mark anything read (there is no per-user read
            // state to update). A normal session also marks all as read.
            const response = this.config.guest
                ? await this.fetchAllAnnouncements(page, perPage)
                : (
                      await Promise.all([
                          this.markAllAsRead(),
                          this.fetchAllAnnouncements(page, perPage),
                      ])
                  )[1];

            list.announcements = response.announcements;
            list.pagination = response.pagination;
            list.isLoading = false;
            list.hasError = false;
            list.updateContent();

            this.config.log(
                `Announcements list loaded with ${response.announcements.length} announcements (page ${page}/${response.pagination?.last_page || 1})`
            );
        } catch (error) {
            this.config.error('Failed to open announcements:', error);
            void this.maybeRecoverSession(error);
            // Keep the modal open on a translated error state rather than
            // vanishing under the user or masquerading as an empty list.
            list.isLoading = false;
            list.hasError = true;
            list.updateContent();
        }
    }

    /**
     * Fetch the latest published announcements as raw data — no UI is rendered —
     * so the host app can build its own "what's new" / changelog surface (e.g. a
     * panel on a login or settings page). Returns the same active + ended
     * announcements as the built-in list, newest first, in the SDK's active
     * language — the `language` you pass to `init()`, or the visitor's
     * auto-detected browser language when you don't set one (falling back to each
     * announcement's default translation if it has none for that language).
     *
     * @param limit maximum number to return (default 3, clamped to 1–20)
     */
    public async getAnnouncements(limit: number = 3): Promise<Announcement[]> {
        this.ensureInitialized();

        const perPage = Number.isFinite(limit) ? Math.min(Math.max(Math.floor(limit), 1), 20) : 3;
        const response = await this.fetchAllAnnouncements(1, perPage);

        return response.announcements;
    }

    /**
     * Return the identifiers for the CURRENT visitor — synchronous, no network
     * call. Use them to honor an opt-out / GDPR "right to be forgotten" request:
     * hand the relevant one to your server, which calls `DELETE /api/v1/users`.
     * Remove an IDENTIFIED visitor by `id` (user_id) or `email`; `anonymousId`
     * identifies a visitor only while they are still anonymous (once identified
     * the server keeps no anonymous record to match), so don't rely on it there.
     */
    public getUser(): CurrentUser {
        this.ensureInitialized();

        return {
            id: this.config.user?.id ?? null,
            email: this.config.user?.email ?? null,
            anonymousId: this.state.getAnonymousId(),
            // Consistent with id/email above, not the server's async view.
            isAnonymous: !(this.config.user?.id || this.config.user?.email),
            metadata: { ...this.config.metadata },
        };
    }

    /**
     * Close any open announcement display
     */
    public close(): void {
        this.closeCurrentComponent();
    }

    /**
     * Mark a specific announcement as read
     */
    public async markAsRead(announcementId: string): Promise<void> {
        // Guest mode tracks no per-user read state; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('markAsRead');
            return;
        }

        this.ensureInitialized();

        try {
            const response = await this.api.markAsRead(announcementId);
            this.state.markAsSeen(announcementId);
            this.state.setUnreadCount(response.unread_count);

            this.config.log('Announcement marked as read:', announcementId);
        } catch (error) {
            this.config.error('Failed to mark as read:', error);
            void this.maybeRecoverSession(error);
        }
    }

    /**
     * Mark all announcements as read
     */
    public async markAllAsRead(): Promise<void> {
        // Guest mode tracks no per-user read state; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('markAllAsRead');
            return;
        }

        this.ensureInitialized();

        try {
            const response = await this.api.markAllAsRead();
            this.state.markAllAsSeen();
            this.state.setUnreadCount(response.unread_count);

            this.config.log('All announcements marked as read');
        } catch (error) {
            this.config.error('Failed to mark all as read:', error);
            void this.maybeRecoverSession(error);
        }
    }

    /**
     * Attach a piece of custom metadata to the current visitor (e.g. plan
     * tier, account age — anything you want to target announcements by).
     * Values are strings or numbers; booleans coerce to "true"/"false", and
     * any other type — or a key/value past the size limits — is dropped with
     * a debug log rather than throwing.
     *
     * Applies to `config.metadata` (and therefore `getUser().metadata`)
     * immediately; the change syncs to the server in a short debounced batch
     * so rapid successive calls collapse into one request. Safe to call
     * before init() completes — it's buffered locally and sent as part of the
     * next `/init` call.
     */
    public setMetadata(name: string, value: MetadataValue | boolean): void {
        // Guest mode never registers a visitor, so there is nothing to attach
        // metadata to; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('setMetadata');
            return;
        }

        // A new change is a documented resume point for a sync that hit the retry
        // cap: reset the failure counter so the backoff series restarts from zero.
        this.metadataFlushFailures = 0;
        this.applyMetadataEntry(name, value, true);
        this.scheduleMetadataFlush();
    }

    /**
     * Remove a single metadata key from the current visitor, locally and (via
     * the debounced flush) on the server.
     */
    public removeMetadata(name: string): void {
        // Guest mode has no visitor metadata to remove; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('removeMetadata');
            return;
        }

        if (typeof name !== 'string' || name.length === 0) {
            return;
        }

        // Strip the key exactly as setMetadata/sanitizeMetadata did, so a
        // DELETE /metadata/{key} targets the same canonical key the value was
        // stored under (removeMetadata('plan_tier') removes 'plantier'), always
        // carries a route-valid key, and can never 404 and wedge the queue. A
        // key that strips to nothing (or is over-long) was never storable, so
        // there is nothing to remove.
        const key = Config.stripKey(name);
        if (key === null) {
            this.config.log('Ignoring removeMetadata for invalid key:', name);
            return;
        }

        // A new change resumes a sync paused at the retry cap (see setMetadata).
        this.metadataFlushFailures = 0;

        delete this.config.metadata[key];
        delete this.metadataPendingSet[key];
        this.metadataPendingRemove.add(key);
        this.metadataGeneration++;

        this.scheduleMetadataFlush();
    }

    /**
     * Remove all custom metadata for the current visitor, locally and (via
     * the debounced flush) on the server.
     */
    public clearMetadata(): void {
        // Guest mode has no visitor metadata to clear; no-op with a one-time warn.
        if (this.config.guest) {
            this.warnGuestUnavailable('clearMetadata');
            return;
        }

        // A new change resumes a sync paused at the retry cap (see setMetadata).
        this.metadataFlushFailures = 0;
        this.config.metadata = {};
        this.metadataPendingSet = {};
        this.metadataPendingRemove.clear();
        this.metadataPendingClear = true;
        this.metadataGeneration++;
        this.metadataClearSeq++;

        this.scheduleMetadataFlush();
    }

    /**
     * Destroy the SDK instance
     */
    public destroy(): void {
        this.destroyed = true;
        this.stopPolling();
        this.clearMetadataFlushTimer();
        this.closeCurrentComponent();
        this.hidePersistentTopBar();
        this.state.clear();
        this.api.setSessionToken(null);
        this.queue = null;

        this.config.log('SDK destroyed');
    }

    /**
     * Render a sample of a given boost type on demand, using built-in sample
     * data and bypassing the queue, targeting and analytics. Intended for
     * development and QA so each UI variant can be triggered in isolation
     * without creating real announcements. Styles are (re)applied so the
     * preview looks exactly like the real thing.
     *
     * `'list'` previews the announcements list; it reuses the live list (real
     * data) and therefore requires a completed init().
     */
    public preview(boostType: BoostType | 'list'): void {
        injectStyles();
        this.closeCurrentComponent();
        this.hidePersistentTopBar();

        if (boostType === 'list') {
            if (this.state.isInitialized) {
                this.openAnnouncements();
            } else {
                this.config.error('preview("list") requires init() to have completed first.');
            }
            return;
        }

        const announcement = this.buildSampleAnnouncement(boostType);
        const showBranding = this.state.getShowBranding();
        const close = () => this.closeCurrentComponent();
        const readMore = () => this.preview('full_page_popup');
        // The Popup opens the CTA URL itself on click; this callback must NOT
        // open it too, or the preview would spawn two tabs.
        const ctaClick = () => {
            this.config.log('preview: CTA clicked');
        };

        let component: Popup | TopBar | PersistentTopBar | Toast;

        switch (boostType) {
            case 'full_page_popup':
                component = new Popup(this.config, announcement, close, ctaClick, showBranding);
                break;
            case 'top_bar':
                component = new TopBar(this.config, announcement, close, readMore);
                break;
            case 'persistent_top_bar':
                component = new PersistentTopBar(this.config, announcement, readMore);
                component.show();
                this.persistentTopBar = component;
                return;
            case 'toast_notification':
                component = new Toast(this.config, announcement, close, readMore, close);
                break;
            default:
                this.config.error(`preview(): unknown boost type "${boostType}"`);
                return;
        }

        component.show();
        this.currentComponent = component;
    }

    /**
     * Build a representative sample announcement for a given boost type, used by
     * {@link preview}.
     */
    private buildSampleAnnouncement(boostType: BoostType): Announcement {
        const samples: Partial<Record<BoostType, { title: string; description: string }>> = {
            full_page_popup: {
                title: 'New: Dark mode is here 🌙',
                description:
                    '<p>We just shipped a beautiful dark mode across the entire app.</p><ul><li>Easier on the eyes at night</li><li>Respects your system preference</li><li>Switch anytime from settings</li></ul>',
            },
            top_bar: {
                title: 'We just shipped faster search across your whole workspace.',
                description: '<p>Search is now up to 10× faster.</p>',
            },
            persistent_top_bar: {
                title: 'Scheduled maintenance this Saturday, 02:00–04:00 UTC.',
                description: '<p>Some services may be briefly unavailable during this window.</p>',
            },
            toast_notification: {
                title: 'Bug fixes shipped',
                description: '<p>We squashed a handful of bugs to make things smoother.</p>',
            },
        };

        const sample = samples[boostType] ?? samples.full_page_popup!;

        return {
            id: 'preview',
            type: 'new_feature',
            boost_type: boostType,
            title: sample.title,
            description: sample.description,
            image_url: null,
            action_button_text: 'Check it out',
            action_button_url: 'https://pulserelease.com',
            action_button_color: null,
            language: this.config.language,
            publish_at: new Date().toISOString(),
            end_at: null,
            created_at: new Date().toISOString(),
        };
    }

    /**
     * Apply the server-driven application theme (accent, corner style, color
     * mode). No-op when the server returned no theme.
     */
    private applyServerTheme(theme?: ServerTheme): void {
        this.applyVars(this.config.getServerThemeVariables(theme));
    }

    /**
     * Write a set of CSS custom properties onto the document root so they
     * cascade over the stylesheet defaults.
     */
    private applyVars(vars: Record<string, string>): void {
        if (typeof document === 'undefined') {
            return;
        }

        Object.entries(vars).forEach(([name, value]) => {
            document.documentElement.style.setProperty(name, value);
        });
    }

    /**
     * Fetch all announcements from API (including past/ended ones) with pagination
     * Used for the announcements list modal
     */
    private async fetchAllAnnouncements(
        page: number = 1,
        perPage: number = 10
    ): Promise<AnnouncementsResponse> {
        // Guest mode reads the list from the session-less public guest endpoint,
        // which returns the same payload shape without any per-user read state.
        const response = this.config.guest
            ? await this.api.getGuestAnnouncements(this.config.language, page, perPage)
            : await this.api.getAllAnnouncements(this.config.language, page, perPage);

        this.config.log(
            `Fetched ${response.announcements.length} announcements (page ${page}/${response.pagination?.last_page || 1})`
        );

        return response;
    }

    /**
     * Fetch boosted announcements from API
     * Used for automatic display
     */
    private async fetchBoostedAnnouncements(): Promise<void> {
        const response = await this.api.getBoostedAnnouncements(this.config.language);

        // If destroy() ran while this request was in flight, don't write back
        // into the just-cleared state (which would also re-persist to storage).
        if (!this.state.isInitialized) {
            return;
        }

        this.state.setAnnouncements(response.announcements);

        this.config.log(`Fetched ${response.announcements.length} boosted announcements`);
    }

    /**
     * Fetch announcements and display boosted ones automatically
     */
    private async fetchAndDisplayAnnouncements(): Promise<void> {
        // Fetch only boosted announcements for automatic display
        await this.fetchBoostedAnnouncements();

        // Bail if the instance was destroyed mid-fetch, so a late poll never
        // renders UI onto a torn-down instance.
        if (!this.state.isInitialized) {
            return;
        }

        // Check for persistent top bar announcements (always show, even if seen)
        const allAnnouncements = this.state.announcements;
        const persistentTopBars = allAnnouncements.filter(
            (a: Announcement) =>
                a.boost_type === 'persistent_top_bar' && this.isPersistentBarActive(a)
        );

        // Display persistent top bar if available. If it throws, that must
        // not prevent the rest of the queue from rendering.
        if (persistentTopBars.length > 0) {
            const persistentBar = persistentTopBars[0]; // Only show one at a time
            try {
                await this.displayPersistentTopBar(persistentBar);
            } catch (error) {
                this.config.error('Failed to display persistent top bar:', error);
            }
        } else {
            // A persistent bar shown earlier is no longer active (ended/removed);
            // a poll should take it down rather than leave it stuck on screen.
            this.hidePersistentTopBar();
        }

        // Get unseen announcements with boost types (excluding persistent_top_bar)
        const unseenAnnouncements = this.state.getUnseenAnnouncements();
        const boostedAnnouncements = unseenAnnouncements.filter(
            (a) => a.boost_type !== 'none' && a.boost_type !== 'persistent_top_bar'
        );

        if (boostedAnnouncements.length === 0) {
            this.config.log('No new boosted announcements to display');
            return;
        }

        // If the user has explicitly opened the announcements list (e.g. clicked
        // "What's new" while init was still in flight), it owns the screen. Don't
        // pop a boosted announcement over it — the fetched data is cached in
        // state, and the next idle poll displays anything unseen once the list is
        // closed. Mirrors pollForAnnouncements' "something on screen" guard.
        if (this.currentComponent instanceof AnnouncementList) {
            this.config.log('Announcements list is open; deferring boosted auto-display');
            return;
        }

        // Create queue and display first announcement
        this.queue = new AnnouncementQueue(boostedAnnouncements);
        await this.displayNextInQueue();
    }

    /**
     * Display the next announcement in queue.
     *
     * If a single announcement fails to render (e.g. malformed payload, DOM
     * exception inside a component), we log and advance instead of stalling
     * the entire queue — one bad item must not silently kill every other
     * announcement the customer paid for.
     */
    private async displayNextInQueue(): Promise<void> {
        if (!this.queue || !this.queue.hasNext()) {
            this.config.log('No more announcements in queue');
            this.queue = null;
            return;
        }

        const announcement = this.queue.getNext();
        if (!announcement) {
            return;
        }

        this.config.log(
            `Displaying announcement: ${announcement.title} (${announcement.boost_type})`
        );

        try {
            await this.displayAnnouncement(
                announcement,
                this.mapBoostTypeToViewType(announcement.boost_type)
            );
        } catch (error) {
            this.config.error(
                `Failed to display announcement ${announcement.id} (${announcement.boost_type}); advancing queue:`,
                error
            );
            // Reset transient state the failed render may have left behind.
            this.currentComponent = null;
            await this.displayNextInQueue();
        }
    }

    /**
     * Display an announcement based on its boost type
     * Note: View tracking only happens on user interaction, not on initial display
     */
    private async displayAnnouncement(
        announcement: Announcement,
        viewType: ViewType
    ): Promise<void> {
        // Create and show appropriate component. Every callback is fired
        // synchronously by the component, so guard each async handler with a
        // catch — a failed tracking request must never reject into the host page.
        const onClose = () =>
            this.fireAndForget(
                this.handleAnnouncementClose(announcement, viewType),
                'Announcement close handler failed'
            );
        const onDismiss = () =>
            this.fireAndForget(
                this.handleAnnouncementDismiss(announcement),
                'Announcement dismiss handler failed'
            );
        const onCtaClick = () =>
            this.fireAndForget(this.handleCtaClick(announcement), 'CTA click handler failed');
        const onReadMore = () =>
            this.fireAndForget(this.handleReadMoreClick(announcement), 'Read-more handler failed');

        let component: Popup | TopBar | PersistentTopBar | Toast;

        switch (announcement.boost_type) {
            case 'full_page_popup':
                component = new Popup(
                    this.config,
                    announcement,
                    onClose,
                    onCtaClick,
                    this.state.getShowBranding()
                );
                break;

            case 'top_bar':
                component = new TopBar(this.config, announcement, onClose, onReadMore);
                break;

            case 'persistent_top_bar':
                component = new PersistentTopBar(this.config, announcement, onReadMore);
                break;

            case 'toast_notification':
                component = new Toast(this.config, announcement, onClose, onReadMore, onDismiss);
                break;

            default:
                return;
        }

        component.show();
        this.currentComponent = component;
    }

    /**
     * Handle announcement close (manual close - marks as read)
     * If viewType is provided, tracks the view (for initial boosted announcements)
     * If viewType is not provided, assumes view is already tracked (for read more/list clicks)
     */
    private async handleAnnouncementClose(
        announcement: Announcement,
        viewType?: ViewType
    ): Promise<void> {
        // Every tracking call below is best-effort: if one fails, the popup must
        // still close, state must still reset, and the queue must still advance —
        // otherwise a single network blip wedges auto-display for the whole
        // session. The state reset + queue-advance therefore live in `finally`.
        try {
            // Track view when user interacts (if not already tracked, and not
            // being tracked right now by a concurrent CTA click on THIS
            // announcement — see viewInFlightId).
            if (viewType && !this.state.currentView && this.viewInFlightId !== announcement.id) {
                try {
                    const view = await this.viewTracker.trackView(announcement.id, viewType);

                    this.state.setCurrentView(view);

                    // Start read time tracking
                    this.readTimeTracker.start(view.id);
                } catch (error) {
                    this.config.error('Failed to track view on close:', error);
                }
            }

            // Stop read time tracking
            try {
                await this.readTimeTracker.stop();
            } catch (error) {
                this.config.error('Failed to stop read time tracker:', error);
            }

            // Mark as seen AND read (manual close). markAsRead swallows its own
            // errors, so this stays non-fatal.
            this.state.markAsSeen(announcement.id);
            await this.markAsRead(announcement.id);
        } finally {
            this.state.setCurrentView(null);
            this.currentComponent = null;

            // Display next in queue after a short delay
            setTimeout(() => {
                void this.displayNextInQueue();
            }, 1000);
        }
    }

    /**
     * Handle announcement dismiss (auto-dismiss - does NOT mark as read)
     */
    private async handleAnnouncementDismiss(announcement: Announcement): Promise<void> {
        try {
            // Stop tracking read time
            await this.readTimeTracker.stop();
        } catch (error) {
            this.config.error('Failed to stop read time tracker:', error);
        } finally {
            // Mark seen (client-side display suppression) but NOT read: an
            // auto-dismiss shouldn't count as the user having read it, yet
            // without suppressing it the periodic poll would re-show the same
            // toast every interval (an in-session nag loop).
            this.state.markAsSeen(announcement.id);

            this.state.setCurrentView(null);
            this.currentComponent = null;

            // Display next in queue after a short delay
            setTimeout(() => {
                void this.displayNextInQueue();
            }, 1000);
        }
    }

    /**
     * Handle CTA click from boosted announcement (needs to track view first)
     */
    private async handleCtaClick(announcement: Announcement): Promise<void>;
    /**
     * Handle CTA click from read more/list (view already tracked)
     */
    private async handleCtaClick(viewId: string): Promise<void>;
    private async handleCtaClick(announcementOrViewId: Announcement | string): Promise<void> {
        let viewId: string;

        // If first parameter is an Announcement, we need to track the view first
        if (typeof announcementOrViewId === 'object') {
            const announcement = announcementOrViewId;
            const viewType = this.mapBoostTypeToViewType(announcement.boost_type);

            // Flag THIS announcement's view as in-flight BEFORE the await so the
            // close handler, which the component fires synchronously right after
            // this CTA callback, skips its own trackView for the same
            // announcement. The popup is closing, so we don't set currentView or
            // start read-time tracking here — this call only needs the view id.
            this.viewInFlightId = announcement.id;
            try {
                const view = await this.viewTracker.trackView(announcement.id, viewType);
                viewId = view.id;
            } finally {
                if (this.viewInFlightId === announcement.id) {
                    this.viewInFlightId = null;
                }
            }
        } else {
            // viewId was passed directly
            viewId = announcementOrViewId;
        }

        this.clickTracker.trackClick(viewId).catch((err) => {
            this.config.error('Failed to track CTA click:', err);
        });
    }

    /**
     * Handle "Read more" click
     * Tracks view for the popup and marks the announcement as read when closed
     */
    private async handleReadMoreClick(announcement: Announcement): Promise<void> {
        // Track click if there was a previous view (shouldn't be, but just in
        // case). Best-effort — a failed click track must not block opening the
        // popup the user asked for.
        if (this.state.currentView) {
            try {
                await this.clickTracker.trackClick(this.state.currentView.id);
            } catch (error) {
                this.config.error('Failed to track read-more click:', error);
            }
        }

        // Mark announcement as seen and read (user is engaging with it)
        this.state.markAsSeen(announcement.id);
        await this.markAsRead(announcement.id);

        // Close current component (if any)
        this.closeCurrentComponent();

        // Track view for the popup. Best-effort: the popup must open even when
        // analytics is unreachable, so a failure here just leaves the view
        // untracked (no CTA/read-time association).
        let view: AnnouncementView | null = null;
        try {
            view = await this.viewTracker.trackView(announcement.id, 'popup');
            this.state.setCurrentView(view);
            this.readTimeTracker.start(view.id);
        } catch (error) {
            this.config.error('Failed to track view for read-more popup:', error);
        }

        const viewId = view?.id;
        const popup = new Popup(
            this.config,
            announcement,
            () =>
                this.fireAndForget(
                    this.handleAnnouncementClose(announcement), // Don't pass viewType - view already tracked
                    'Announcement close handler failed'
                ),
            () => {
                if (viewId) {
                    this.fireAndForget(this.handleCtaClick(viewId), 'CTA click handler failed');
                }
            },
            this.state.getShowBranding()
        );

        popup.show();
        this.currentComponent = popup;
    }

    /**
     * Handle announcement item click from list
     * Tracks view and marks as read when closed
     */
    private async handleAnnouncementClick(announcement: Announcement): Promise<void> {
        // Remember which page of the list the user was on so "back" reopens the
        // list exactly where they left it instead of resetting to page 1.
        const onBack = this.buildBackToListCallback();

        // Guest mode: open the full view WITHOUT any analytics or read-state
        // writes (no trackers exist, and there is no per-user read state). The
        // Popup opens its own CTA URL, so the CTA callback is a no-op here.
        if (this.config.guest) {
            const popup = new Popup(
                this.config,
                announcement,
                () => this.handleComponentClose(),
                () => {},
                this.state.getShowBranding(),
                onBack
            );
            // Show the popup before hiding the list so the shared scroll lock is
            // never released between the two modals.
            popup.show();
            this.closeCurrentComponent();
            this.currentComponent = popup;
            return;
        }

        // Track view. Best-effort: a failed track must not prevent opening the
        // announcement the user clicked, nor reject into the list component.
        let view: AnnouncementView | null = null;
        try {
            view = await this.viewTracker.trackView(announcement.id, 'list');
            this.state.setCurrentView(view);
            this.readTimeTracker.start(view.id);
        } catch (error) {
            this.config.error('Failed to track view for list click:', error);
        }

        // Show popup
        const viewId = view?.id;
        const popup = new Popup(
            this.config,
            announcement,
            () =>
                this.fireAndForget(
                    this.handleAnnouncementClose(announcement), // Don't pass viewType - view already tracked
                    'Announcement close handler failed'
                ),
            () => {
                if (viewId) {
                    this.fireAndForget(this.handleCtaClick(viewId), 'CTA click handler failed');
                }
            },
            this.state.getShowBranding(),
            onBack
        );

        // Show the popup before hiding the list so the shared scroll lock stays
        // held across the hand-off. Hide the OUTGOING component directly rather
        // than via closeCurrentComponent(): the latter also stops the read-time
        // tracker (its generic stop is for paths where the closing component
        // owns the active view), which would immediately kill the view we just
        // started tracking for THIS popup — recording 0 read seconds. The list
        // never owns a read-time view, so hiding it directly is safe and the
        // popup's tracking keeps running until it is closed.
        const previous = this.currentComponent;
        popup.show();
        previous?.hide();
        this.currentComponent = popup;
    }

    /**
     * Capture the list's current page/per-page (if the list is what's on screen)
     * and return a callback that reopens the list at that same page. Returns
     * undefined when there is no list to return to, so the popup renders without
     * a back affordance.
     */
    private buildBackToListCallback(): (() => void) | undefined {
        if (!(this.currentComponent instanceof AnnouncementList)) {
            return undefined;
        }

        const page = this.currentComponent.pagination?.current_page ?? 1;
        const perPage = this.currentComponent.pagination?.per_page ?? 10;

        return () =>
            this.fireAndForget(
                this.showAnnouncementsList(page, perPage),
                'Back to announcements list failed'
            );
    }

    /**
     * Close current component
     */
    private closeCurrentComponent(): void {
        if (this.currentComponent) {
            this.currentComponent.hide();
            this.currentComponent = null;
        }

        if (this.state.currentView) {
            this.readTimeTracker.stop().catch((err) => {
                this.config.error('Failed to stop read time tracker:', err);
            });

            this.state.setCurrentView(null);
        }
    }

    /**
     * Handle component close (without marking as read)
     */
    private handleComponentClose(): void {
        this.closeCurrentComponent();
    }

    /**
     * Wrap an async component callback so a rejected tracking/display promise is
     * logged instead of escaping unhandled into the customer's page. UI
     * components fire these callbacks synchronously (they don't await them), so
     * without this a network failure inside a handler surfaces as an unhandled
     * rejection on the host page.
     *
     * Returns a promise that always resolves (never rejects), so callers that do
     * choose to await it can, without ever seeing the rejection.
     */
    private fireAndForget(promise: Promise<unknown>, context: string): Promise<void> {
        return promise.then(
            () => undefined,
            (error) => {
                this.config.error(`${context}:`, error);
            }
        );
    }

    /**
     * Map boost type to view type
     */
    private mapBoostTypeToViewType(boostType: BoostType): ViewType {
        switch (boostType) {
            case 'full_page_popup':
                return 'popup';
            case 'top_bar':
                return 'topbar';
            case 'persistent_top_bar':
                return 'persistent_topbar';
            case 'toast_notification':
                return 'toast';
            default:
                return 'list';
        }
    }

    /**
     * Check if persistent bar is still active (hasn't reached end date)
     */
    private isPersistentBarActive(announcement: Announcement): boolean {
        if (!announcement.end_at) {
            return true; // No end date means it's always active
        }

        const endDate = new Date(announcement.end_at);
        const now = new Date();
        return now < endDate;
    }

    /**
     * Display persistent top bar
     */
    private async displayPersistentTopBar(announcement: Announcement): Promise<void> {
        // Already showing this exact announcement (e.g. on a poll tick) — leave
        // it in place rather than tearing it down and re-running the slide-in.
        if (this.persistentTopBar && this.persistentTopBarId === announcement.id) {
            return;
        }

        // Close existing persistent top bar if any
        if (this.persistentTopBar) {
            this.persistentTopBar.hide();
            this.persistentTopBar = null;
            this.persistentTopBarId = null;
        }

        this.config.log(`Displaying persistent top bar: ${announcement.title}`);

        // Wrapped like every other component callback: the bar fires this
        // synchronously without awaiting it, so an unguarded rejection would
        // escape into the host page.
        const onReadMore = () =>
            this.fireAndForget(this.handleReadMoreClick(announcement), 'Read-more handler failed');

        const component = new PersistentTopBar(this.config, announcement, onReadMore);

        component.show();
        this.persistentTopBar = component;
        this.persistentTopBarId = announcement.id;
    }

    /**
     * Hide the persistent bar if one is showing. Used when a poll finds the
     * previously shown persistent announcement is no longer active (ended or
     * deleted) so it disappears without waiting for a reload.
     */
    private hidePersistentTopBar(): void {
        if (this.persistentTopBar) {
            this.persistentTopBar.hide();
            this.persistentTopBar = null;
            this.persistentTopBarId = null;
        }
    }

    /**
     * Ensure SDK is initialized
     */
    private ensureInitialized(): void {
        if (!this.state.isInitialized) {
            throw new Error('PulseRelease SDK is not initialized. Call init() first.');
        }
    }
}
